import { fetchData } from '@eliadmin/core/src/api/fetcher';
import {
  SPAREPARTS_PROXY_PATH,
  getSparepartsErrorMessage,
  isSupportedSparepartsPath,
  normalizeSparepartsPath,
  safeSparepartsActivityId,
} from '../config/spareparts-api';

interface SparepartsApiResponse<T = any> {
  data: T | null;
  success: boolean;
  message?: string;
  status?: number;
  code?: string;
  activityId?: string;
}

interface ApiRequestConfig {
  body?: any;
  apiKey?: string;
  responseType?: 'json' | 'blob';
}

// Kept for existing project consumers; the actual key lives in the server runtime env.
export const SPAREPARTS_API_KEY = '';

// Brand interface based on API documentation
export interface Brand {
  id: string; // GUID
  name: string | null;
  legacyId?: number;
  shortName: string | null;
  logoPath?: string;
  logoGrayscalePath?: string;
  logoTransparentPath?: string;
  logoTransparentBackgroundColor?: string;
  brandType?: string;
  sortOrder?: number;
}

// Model Specification interface based on API documentation
export interface ModelSpecification {
  specificationName: string;
}

// Model Types enum based on API documentation
export enum ModelTypes {
  None = 0,
  Motorcycle = 1,
  Car = 1 << 1,
  WP = 1 << 2,
  Bicycle = 1 << 3,
  Battery = 1 << 4,
  Atv = 1 << 5,
  Offroad = 1 << 11,
  Street = 1 << 12,
  WPDefault = 1 << 15,
  WPShop = 1 << 16,
  OffroadBike = Motorcycle | Offroad,
  StreetBike = Motorcycle | Street,
  StandardWP = WP | WPDefault,
  ShopWP = WP | WPShop,
}

// Asset interface for model assets
export interface Asset {
  id: string;
  assetPath: string;
}

// Model interface based on actual API response
export interface Model {
  id: string; // GUID
  modelStructurePath: string | null;
  brandId?: string | null; // GUID
  brandTenantId?: string; // GUID
  description?: string;
  articleNumber?: string;
  modelTypes: string; // Changed from ModelTypes enum to string based on actual response
  specifications?: string | null;
  year?: number | null;
  color?: string | null;
  engineArticleNumber?: string | null;
  engineCylinderCount?: number | null;
  assetPath: string;
  assets?: Asset[];
  fallbackAssetPath: string;
  segmentName?: string | null;
  hasChildren: boolean;
  co2Emissions?: number | null;
  engineDisplacement?: number | null;
}

// WP Article Model interface (extends Model with isOem property)
export interface WpArticleModel extends Model {
  isOem: boolean;
}

// Component Type enum
export enum ComponentType {
  Chassis = 'Chassis',
  Engine = 'Engine',
  Fork = 'Fork',
  Shock = 'Shock',
  SteeringDamper = 'SteeringDamper',
  BicycleParts = 'BicycleParts',
  BicycleAccessories = 'BicycleAccessories',
  BicycleGeneral = 'BicycleGeneral',
}

// Product Type enum
export enum ProductType {
  Bike = 'Bike',
  Car = 'Car',
  Suspension = 'Suspension',
  Bicycle = 'Bicycle',
}

// SparePartType enum
export enum SparePartType {
  Part = 'Part',
  SpecialPart = 'SpecialPart',
}

// Component Group interface
export interface ComponentGroup {
  id: string; // GUID
  parentId?: string; // GUID
  parent?: ComponentGroup; // Parent component group
  code: string;
  description: string;
  assetPath?: string; // URI pointing to image of the article
  fallbackAssetPath?: string; // Fallback path of URI pointing to image of the article
  productType: ProductType;
  componentType: ComponentType;
}

// Component Group Template interface
export interface ComponentGroupTemplate {
  id: string; // GUID
  componentGroupId: string; // GUID
  componentGroup: ComponentGroup;
  isDeactivated: boolean;
  assetPathLowResolution?: string; // Path for low resolution asset
  assetPathMediumResolution?: string; // Path for medium resolution asset
  assetPathHighResolution?: string; // Path for high resolution asset
}

// Component Group Templates Response interface
export interface ComponentGroupTemplatesResponse {
  componentGroupTemplates: ComponentGroupTemplate[];
}

// Article interface for spare parts
export interface Article {
  id: string; // GUID
  name?: string; // Article name
  articleNumber: string; // Article number
  measureUnit: string; // Measuring unit for the article
  isActive: boolean; // Indicator if article is active
  decimalPlaces?: number; // Legacy field, not returned by the current public API
  orderable?: boolean;
  isLabor?: boolean;
  environmentGroup?: string; // Environment group showing if article is dangerous
  assetPath?: string; // URI pointing to image of the article
}

// Hotspot interface for spare parts
export interface Hotspot {
  id: string; // GUID
  startX: number; // Start of hotspot region rectangle in horizontal direction (normalized 0-1)
  startY: number; // Start of hotspot region rectangle in vertical direction (normalized 0-1)
  endX: number; // End of hotspot region rectangle in horizontal direction (normalized 0-1)
  endY: number; // End of hotspot region rectangle in vertical direction (normalized 0-1)
}

// SparePart interface
export interface SparePart {
  id: string; // GUID
  article: Article; // The article which the spare part is associated
  componentGroupTemplateId: string; // GUID - component group template identifier
  quantity: number; // The quantity of the spare part
  lineNumber: number; // The specified line number
  sortOrder: number; // The specified sort order
  type: SparePartType; // The type of the given spare part
  isNew: boolean; // Indicator if spare part is new or not
  hotspots: Hotspot[]; // The hot spots of the given spare part
}

// SpareParts Response interface
export interface SparePartsResponse {
  spareParts: SparePart[];
}

// Usage List interfaces
export interface SortItem {
  field:
    | 'ArticleNumber'
    | 'ArticleName'
    | 'ComponentGroupDescription'
    | 'ModelArticleNumber'
    | 'ModelYear'
    | 'ModelVariant'
    | 'ComponentGroupType';
  direction: 'Asc' | 'Desc';
}

export interface FilterItem {
  field:
    | 'ArticleNumber'
    | 'ArticleName'
    | 'ComponentGroupDescription'
    | 'ModelArticleNumber'
    | 'ModelYear'
    | 'ModelVariant'
    | 'ComponentGroupType';
  value: string;
  operation: 'Contains' | 'IsEqual';
}

export interface FilterItemsJoinedByOperator {
  filterItems: FilterItem[];
  filterItemsOperator: 'And' | 'Or';
}

export interface UsageListQueryParameters {
  pageIndex: number;
  pageSize: number;
  sortItems: SortItem[];
  filterItemsJoinedByOperator: FilterItemsJoinedByOperator;
  cultureCode: string;
  fullTextFilter?: string;
}

export interface ComponentGroupItem {
  assetPathHighResolution?: string;
  assetPathMediumResolution?: string;
  assetPathLowResolution?: string;
  componentGroupId: string; // GUID
  componentGroupTemplateId: string; // GUID
  componentGroup: ComponentGroup;
  sparePartId: string; // GUID
  componentGroupDescription: string;
  componentGroupType: string; // Changed from ComponentType enum to string
  articleName: string;
  articleNumber: string;
  modelArticleDescription?: string;
  modelArticleNumber?: string;
  modelYear?: number;
  modelVariant?: string;
}

export interface UsageListResponse {
  totalPages: number;
  totalItems: number;
  currentPage: number;
  maxPageSize: number;
  items: ComponentGroupItem[];
}

async function makeApiRequest<T>(
  endpoint: string,
  { body, apiKey, responseType = 'json' }: ApiRequestConfig,
): Promise<SparepartsApiResponse<T>> {
  const normalizedEndpoint = normalizeSparepartsPath(endpoint);
  if (!normalizedEndpoint || !isSupportedSparepartsPath(normalizedEndpoint) || body !== undefined) {
    return {
      data: null,
      success: false,
      status: 501,
      code: 'SPAREPARTS_UNSUPPORTED',
      message: getSparepartsErrorMessage(501),
    };
  }
  try {
    const params = new URLSearchParams({ path: normalizedEndpoint });
    const headers: Record<string, string> = {
      Accept: responseType === 'blob' ? 'application/pdf' : 'application/json',
    };
    if (apiKey) headers['X-Spareparts-Api-Key'] = apiKey;
    const response = await fetch(`${SPAREPARTS_PROXY_PATH}?${params.toString()}`, {
      method: 'GET',
      headers,
      cache: 'no-store',
      signal: AbortSignal.timeout(20_000),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      const code =
        typeof error?.code === 'string' && /^SPAREPARTS_[A-Z_]{1,40}$/.test(error.code)
          ? error.code
          : undefined;
      return {
        data: null,
        success: false,
        status: response.status,
        code,
        message: getSparepartsErrorMessage(response.status, code),
        activityId: safeSparepartsActivityId(error?.activityId),
      };
    }
    const data = responseType === 'blob' ? await response.blob() : await response.json();
    return {
      data,
      success: true,
      status: response.status,
    };
  } catch (error) {
    const isTimeout = error instanceof Error && ['TimeoutError', 'AbortError'].includes(error.name);
    const status = isTimeout ? 504 : 502;
    return {
      data: null,
      success: false,
      status,
      message: getSparepartsErrorMessage(status),
    };
  }
}

// 1. Get brands
export async function getBrands({ apiKey }: ApiRequestConfig) {
  return makeApiRequest<Brand[]>('/brands', { apiKey });
}

// 2. Get model specifications
export async function getModelSpecifications({ body, apiKey }: ApiRequestConfig) {
  const query = body?.cultureCode ? `?cultureCode=${encodeURIComponent(body.cultureCode)}` : '';
  return makeApiRequest<ModelSpecification[]>(`/models/specifications${query}`, { apiKey });
}

// 3. VIN/Engine number search
export async function searchByVin({
  body,
  apiKey,
}: ApiRequestConfig & { body?: SearchByVinRequest }) {
  const identificationNumber = body?.identificationNumber || body?.vin || body?.engineNumber;
  const queryParams = identificationNumber
    ? `?identificationNumber=${encodeURIComponent(identificationNumber)}`
    : '';
  return makeApiRequest<Model[]>(`/models/vin-search${queryParams}`, {
    body: undefined,
    apiKey,
  });
}

// 4. Get models by article number
export async function getModelsByArticleNumber(
  articleNumber: string,
  { body, apiKey }: ApiRequestConfig & { body?: GetModelsByArticleNumberRequest },
) {
  const includeParents = body?.includeParents || false;
  const queryParams = includeParents ? `?includeParents=${includeParents}` : '';
  return makeApiRequest<Model[]>(`/models/${articleNumber}${queryParams}`, {
    body: undefined,
    apiKey,
  });
}

// 5. Full-text search for models
export async function searchModels({
  body,
  apiKey,
}: ApiRequestConfig & { body?: SearchModelsRequest }) {
  const query = body?.query || '';
  const brandId = body?.brandId;
  const specification = body?.specification;
  const includeModelTypes = body?.includeModelTypes;
  const excludeModelTypes = body?.excludeModelTypes;

  // Build query parameters
  const params = new URLSearchParams();
  if (query) params.append('query', query);
  if (brandId) params.append('brandId', brandId);
  if (specification) params.append('specification', specification);
  if (includeModelTypes && Array.isArray(includeModelTypes)) {
    includeModelTypes.forEach(type => params.append('includeModelTypes', type));
  }
  if (excludeModelTypes && Array.isArray(excludeModelTypes)) {
    excludeModelTypes.forEach(type => params.append('excludeModelTypes', type));
  }

  const queryString = params.toString();
  const endpoint = `/models/fulltext-search${queryString ? `?${queryString}` : ''}`;

  return makeApiRequest<Model[]>(endpoint, { body: undefined, apiKey });
}

// Request body interfaces
export interface GetModelsRequest {
  parentPath?: string;
  depth?: number;
  brandId?: string;
  specification?: string;
}

export interface SearchByVinRequest {
  identificationNumber?: string;
  vin?: string;
  engineNumber?: string;
}

export interface GetModelsByArticleNumberRequest {
  includeParents?: boolean;
}

export interface SearchModelsRequest {
  query?: string;
  brandId?: string;
  specification?: string;
  includeModelTypes?: string[];
  excludeModelTypes?: string[];
}

export interface GetComponentGroupsRequest {
  productTypes?: string[];
  excludeEmpty?: boolean;
  cultureCode?: string;
}

export interface GetComponentGroupRequest {
  articleNumber?: string;
  category?: 'engine' | 'frame';
  cultureCode?: string;
}

// 6. Get models (with optional parent and depth)
export async function getModels({ body, apiKey }: ApiRequestConfig & { body?: GetModelsRequest }) {
  const parentPath = body?.parentPath;
  const depth = body?.depth;
  const brandId = body?.brandId;

  // Build query parameters
  const params = new URLSearchParams();
  if (parentPath) params.append('parentPath', parentPath);
  if (depth !== undefined) params.append('depth', depth.toString());
  if (brandId) params.append('brandId', brandId);

  if (body?.specification) params.append('specification', body.specification);

  const queryString = params.toString();
  const endpoint = `/models${queryString ? `?${queryString}` : ''}`;

  return makeApiRequest<Model[]>(endpoint, { body: undefined, apiKey });
}

// 7. Get compatible WP Articles
export async function getCompatibleWpArticles(articleNumber: string, { apiKey }: ApiRequestConfig) {
  return makeApiRequest<WpArticleModel[]>(`/models/${articleNumber}/wp-articles`, {
    body: undefined,
    apiKey,
  });
}

// 8. Get component group templates (replaces usage list)
export async function getComponentGroup({
  body,
  apiKey,
}: ApiRequestConfig & { body?: GetComponentGroupRequest }) {
  const articleNumber = body?.articleNumber;
  const category = body?.category || 'frame'; // 'engine' or 'frame'
  const cultureCode = body?.cultureCode;

  if (!articleNumber) {
    return {
      data: null,
      success: false,
      message: 'Article number is required',
    };
  }

  if (category === 'engine') {
    return getEngineComponentGroupTemplates(articleNumber, { body: { cultureCode }, apiKey });
  } else {
    return getComponentGroupTemplates(articleNumber, { body: { cultureCode }, apiKey });
  }
}

// 9. Get component groups
export async function getComponentGroups({
  body,
  apiKey,
}: ApiRequestConfig & { body?: GetComponentGroupsRequest }) {
  const productTypes = body?.productTypes;
  const excludeEmpty = body?.excludeEmpty ?? true;
  const cultureCode = body?.cultureCode;

  // Build query parameters
  const params = new URLSearchParams();
  if (excludeEmpty !== undefined) params.append('excludeEmpty', excludeEmpty.toString());
  if (cultureCode) params.append('cultureCode', cultureCode);
  if (productTypes && Array.isArray(productTypes)) {
    productTypes.forEach(type => params.append('productTypes', type));
  }

  const queryString = params.toString();
  const endpoint = `/component-groups${queryString ? `?${queryString}` : ''}`;

  return makeApiRequest<ComponentGroup[]>(endpoint, { body: undefined, apiKey });
}

// 10. Get component group for article
export async function getArticleComponentGroup(
  articleNumber: string,
  { body, apiKey }: ApiRequestConfig,
) {
  return makeApiRequest(`/articles/${articleNumber}/component-group`, { body, apiKey });
}

// 11. Get engine component group templates (legacy - use getEngineComponentGroupTemplatesV2)
export async function getEngineComponentGroupTemplatesLegacy(
  articleNumber: string,
  { body, apiKey }: ApiRequestConfig,
) {
  return makeApiRequest(`/articles/${articleNumber}/engine/component-group-templates`, {
    body,
    apiKey,
  });
}

// 12. Get spare parts for component group template (legacy - use getComponentGroupTemplateSparePartsV2)
export async function getComponentGroupTemplateSparePartsLegacy(
  componentGroupTemplateId: string,
  { body, apiKey }: ApiRequestConfig,
) {
  return makeApiRequest(`/templates/${componentGroupTemplateId}/spare`, { body, apiKey });
}

// 13. Get consumables for article
export async function getArticleConsumables(
  articleNumber: string,
  { body, apiKey }: ApiRequestConfig,
) {
  return makeApiRequest(`/articles/${articleNumber}/consumables`, { body, apiKey });
}

// 14. Get repair times
export async function getRepairTimes(componentGroupId: string, { body, apiKey }: ApiRequestConfig) {
  return makeApiRequest(`/component-groups/${componentGroupId}/repair-times`, { body, apiKey });
}

// 15. Download PDF document
export async function downloadComponentGroupPdf(
  articleNumber: string,
  componentGroupTemplateId: string,
  { body, apiKey }: ApiRequestConfig,
) {
  const query = body?.cultureCode ? `?cultureCode=${encodeURIComponent(body.cultureCode)}` : '';
  return makeApiRequest<Blob>(
    `/downloads/${encodeURIComponent(articleNumber)}/${encodeURIComponent(componentGroupTemplateId)}${query}`,
    {
      apiKey,
      responseType: 'blob',
    },
  );
}

// 16. Get component group templates for frame model
export async function getComponentGroupTemplates(
  articleNumber: string,
  { body, apiKey }: ApiRequestConfig,
) {
  const cultureCode = body?.cultureCode;
  const queryParams = cultureCode ? `?cultureCode=${encodeURIComponent(cultureCode)}` : '';

  return makeApiRequest<ComponentGroupTemplatesResponse>(
    `/articles/${encodeURIComponent(articleNumber)}/component-group-templates${queryParams}`,
    { body: undefined, apiKey },
  );
}

// 17. Get component group templates for engine model
export async function getEngineComponentGroupTemplates(
  articleNumber: string,
  { body, apiKey }: ApiRequestConfig,
) {
  const cultureCode = body?.cultureCode;
  const queryParams = cultureCode ? `?cultureCode=${encodeURIComponent(cultureCode)}` : '';

  return makeApiRequest<ComponentGroupTemplatesResponse>(
    `/articles/${encodeURIComponent(articleNumber)}/engine/component-group-templates${queryParams}`,
    { body: undefined, apiKey },
  );
}

// 18. Get spare parts of a component group template
export async function getComponentGroupTemplateSpareParts(
  componentGroupTemplateId: string,
  { body, apiKey }: ApiRequestConfig,
) {
  const query = body?.cultureCode ? `?cultureCode=${encodeURIComponent(body.cultureCode)}` : '';
  return makeApiRequest<SparePartsResponse>(
    `/component-group-templates/${encodeURIComponent(componentGroupTemplateId)}/spare-parts${query}`,
    { body: undefined, apiKey },
  );
}

// 19. Get a specific component group template by ID
export async function getComponentGroupTemplate(
  componentGroupTemplateId: string,
  { body, apiKey }: ApiRequestConfig,
): Promise<SparepartsApiResponse<ComponentGroupTemplate>> {
  return makeApiRequest<ComponentGroupTemplate>(
    `/component-group-templates/${componentGroupTemplateId}`,
    { body, apiKey },
  );
}

// 20. Get usage list
export async function getUsageList({
  body,
  apiKey,
}: ApiRequestConfig & { body?: UsageListQueryParameters }) {
  return makeApiRequest<UsageListResponse>('/articles/usage-list', { body, apiKey });
}

// 21. Products lookup interface
export interface ProductsLookupRequest {
  type: string;
  term: string;
}

// 22. Products lookup response interface
export interface ProductsLookupResponse {
  // Define the response structure based on your API documentation
  // This is a placeholder - adjust based on actual API response
  products: any[];
  totalCount: number;
  success: boolean;
}

// 23. Products lookup using fetchData pattern
export async function lookupProducts({
  body,
}: ApiRequestConfig & { body: ProductsLookupRequest; locale: string }) {
  return fetchData<ProductsLookupResponse>({
    method: 'POST',
    uri: '/store/products/lookup',
    body,
    locale: 'en', // Default locale, can be made configurable
  });
}

// Export types for use in components
export type { SparepartsApiResponse, ApiRequestConfig };
